import {ref} from 'vue';
import {clientCompanyProfileEndpoints, uploadImage} from '@configuration';
import isEmpty from 'lodash-es/isEmpty';
import {defineStore} from 'pinia';
import {useTranslation} from '#imports';
import {useErrorHandler} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {ClientCompanyProfileResource} from '@api';
import {FetchError} from '@api';
import type {CompanyProfileForm} from '../types';
import {completeNotification, filterObject, useGetCompanyId} from '@/common/utils';

export const useProfileStore = defineStore('profileStore', () => {
    const {t} = useTranslation();
    const companyId = useGetCompanyId();

    const profileData = ref<ClientCompanyProfileResource | null>(null);
    const loading = ref(false);
    const errorHandler = useErrorHandler();

    async function fetchCompanyProfileData(): Promise<void> {
        try {
            loading.value = true;
            const companyProfileData = await clientCompanyProfileEndpoints.getCompanyProfile({companyId});

            if (companyProfileData) {
                profileData.value = companyProfileData;
            } else {
                throw new Error('No profile');
            }
        } catch (e) {
            errorHandler.handleFatalError(t('client.settings.profile.get.error'), 404);
            return Promise.reject(e);
        } finally {
            loading.value = false;
        }
    }
    async function updateCompanyProfile({logo, ...rest}: CompanyProfileForm): Promise<void> {
        try {
            const filtered = filterObject(rest, (params) => params.value != null);
            const finalForm: ClientCompanyProfileResource = {
                ...filtered,
            };
            if (logo instanceof File) {
                await uploadImage(logo);
            }
            if (!isEmpty(finalForm)) {
                const companyProfileData = await clientCompanyProfileEndpoints.updateCompanyProfile({
                    companyId,
                    clientCompanySettingsProfileUpdateRequest: finalForm,
                });
                if (companyProfileData) {
                    profileData.value = companyProfileData;
                }
            }
            pushVJoySnackbar({props: {message: t('client.settings.profile.post.success')}});
            completeNotification();
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 400 && e.hasValidationErrors()) {
                const validationErrors = e.getValidationErrors(t);
                if ('description' in validationErrors) {
                    const errorMessage = validationErrors.description[0];
                    pushVJoySnackbar({props: {message: errorMessage, level: 'error'}});
                    return Promise.reject(e);
                }
            }
            pushVJoySnackbar({props: {message: t('client.settings.profile.post.error'), level: 'error'}});
            return Promise.reject(e);
        }
    }

    return {
        profileData,
        fetchCompanyProfileData,
        updateCompanyProfile,
        loading,
    };
});
